import { useLanguage } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

interface SkillCategory {
  id: number;
  title: string;
  skills: Skill[];
}

interface Skill {
  name: string;
  level: number;
}

export const SkillsSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  // Sample skills data (replace with real data)
  const skillsData: SkillCategory[] = [
    {
      id: 1,
      title: t("skills.frontend"),
      skills: [
        { name: "JavaScript / TypeScript", level: 90 },
        { name: "HTML5 / CSS3", level: 95 },
        { name: "React / Vue js", level: 80 },
        { name: "Tailwind / Bootstrap", level: 80 },
        { name: "Web Components", level: 80 },
        { name: "Performance - Web Vitals", level: 90 },
        { name: "Web Accessibility", level: 70 },
        { name: "Vitest", level: 80 },
        { name: "JQuery", level: 95 },
      ],
    },
    {
      id: 2,
      title: t("skills.backend"),
      skills: [
        { name: "Node.js", level: 70 },
        { name: "PHP", level: 70 },
        { name: "Database - MySQL, NoSQL", level: 70 },
        { name: "HMC, Opencart e-commerce", level: 75 },
        { name: "Cyber Security", level: 80 },
        { name: "Android Programming", level: 75 },
      ],
    },
    {
      id: 3,
      title: t("skills.tools"),
      skills: [
        { name: "Git, Bitbucket", level: 95 },
        { name: "Jira, Confluence", level: 95 },
        { name: "Google Lighthouse, Core Web Vitals", level: 95 },
        { name: "Copilot & AI Tools", level: 90 },
        { name: "Dev Tools", level: 95 },
        { name: "Analytics & Tracking Tools", level: 80 },
      ],
    },
    {
      id: 4,
      title: t("skills.improve"),
      skills: [
        { name: "AWS", level: 60 },
        { name: "Drupal", level: 60 },
        { name: "CSSLP", level: 60 },
        { name: "Next js", level: 60 },
      ],
    },
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <section
      id="skills"
      ref={sectionRef}
      className="py-20 px-6 md:px-12 lg:px-24 relative"
    >
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6 }}
        className="text-3xl font-bold mb-16 text-center"
      >
        {t("skills.title")}
      </motion.h2>

      <motion.div
        variants={container}
        initial="hidden"
        animate={isInView ? "show" : "hidden"}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-7xl mx-auto"
      >
        {skillsData.map((category) => (
          <motion.div
            key={category.id}
            variants={item}
            className="bg-secondary/20 backdrop-blur-sm p-6 rounded-lg border border-accent/20"
          >
            <h3 className="text-xl font-bold mb-6 text-center text-accent">
              {category.title}
            </h3>
            <div className="space-y-6">
              {category.skills.map((skill, index) => (
                <div key={index}>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">{skill.name}</span>
                    <span className="text-muted-foreground">
                      {skill.level}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={
                        isInView ? { width: `${skill.level}%` } : { width: 0 }
                      }
                      transition={{ duration: 1, delay: 0.3 + index * 0.1 }}
                      className="h-full bg-accent rounded-full"
                    ></motion.div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};
